import { ApiClient, ApiClientError } from '@/lib/axios/client';
import { axiosInstance } from '@/lib/axios/config';
import type {
  AIChatDocument,
  AIChatSession,
  AIChatSessionWithMessages,
  AIChatMessageResponse,
  AIChatStreamHandlers,
  AIErrorResponse,
  CreateAIChatSessionRequest,
  SendAIChatMessageRequest,
  SendAIChatFeedbackRequest,
  SessionStatus,
  AIQuotaStatus,
  AIDraftView,
  AIDraftExport,
  AIDraftExportResult,
} from './types';

const BASE_PATH = '/api/v1/ai/chat';

/** The server sends a heartbeat every 15 seconds, so this much silence is a dead connection */
const STREAM_SILENCE_LIMIT_MS = 45_000;
const TURN_POLL_INTERVAL_MS = 5_000;
/** The server ends a turn after 5 minutes */
const TURN_WAIT_LIMIT_MS = 6 * 60_000;

/** The connection failed or went silent. The turn may still be running on the server. */
class AIStreamLostError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AIStreamLostError';
  }
}

/** Payloads of the SSE events emitted by the streaming endpoint */
interface StreamDataEnvelope {
  data?: string;
}

interface StreamMessageEnvelope {
  status?: string;
  data?: AIChatMessageResponse;
}

/** Builds an axios-shaped error so extractAIError() works on stream errors */
function toAIError(payload: Record<string, unknown>): Error {
  const message = typeof payload.message === 'string' ? payload.message : 'AI request failed';
  if ('error_code' in payload) {
    return Object.assign(new Error(message), {
      response: { data: { data: payload as unknown as AIErrorResponse } },
    });
  }
  return new Error(message);
}

/** Reading the session failed because it is gone or isn't the caller's, not for a passing reason */
export function isSessionGoneError(error: unknown): boolean {
  return error instanceof ApiClientError && (error.status === 403 || error.status === 404);
}

/** The error for a stream the server refused, with the server's own message when it sent one */
async function refusedStreamError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as { data?: Record<string, unknown> } | null;
  if (body?.data && typeof body.data.message === 'string') {
    return toAIError(body.data);
  }
  // A proxy's error page: the request may have reached the server
  if (response.status >= 500) {
    return new AIStreamLostError(`HTTP ${response.status}`);
  }
  return new Error(`The assistant is unavailable (HTTP ${response.status}). Try again.`);
}

export class AIChatApi {
  /**
   * Create a new AI chat session
   */
  static async createSession(data: CreateAIChatSessionRequest): Promise<AIChatSession> {
    return await ApiClient.post<AIChatSession>(`${BASE_PATH}/sessions`, data);
  }

  /**
   * List the caller's sessions (for resumption)
   */
  static async listSessions(status?: SessionStatus): Promise<AIChatSession[]> {
    const query = status ? `?status=${status}` : '';
    return await ApiClient.get<AIChatSession[]>(`${BASE_PATH}/sessions${query}`);
  }

  /**
   * Get a session with its message history
   */
  static async getSession(sessionId: string): Promise<AIChatSessionWithMessages> {
    return await ApiClient.get<AIChatSessionWithMessages>(`${BASE_PATH}/sessions/${sessionId}`);
  }

  /**
   * Delete/abandon a session
   */
  static async deleteSession(sessionId: string): Promise<void> {
    await ApiClient.delete(`${BASE_PATH}/sessions/${sessionId}`);
  }

  /**
   * Send a message to the AI and get a response
   */
  static async sendMessage(sessionId: string, data: SendAIChatMessageRequest): Promise<AIChatMessageResponse> {
    return await ApiClient.post<AIChatMessageResponse>(`${BASE_PATH}/sessions/${sessionId}/messages`, data);
  }

  /**
   * Send a message to the AI, streaming the assistant turn over SSE, and resolve with
   * the same final response as sendMessage. The server saves a turn's reply even if the
   * connection drops, so a lost stream falls back to waiting for the saved reply.
   */
  static async sendMessageStream(
    sessionId: string,
    data: SendAIChatMessageRequest,
    handlers: AIChatStreamHandlers = {}
  ): Promise<AIChatMessageResponse> {
    // This axios call refreshes an expired access token, which the fetch below can't do.
    // Its message count tells this turn's reply from an earlier one.
    const before = await AIChatApi.getSession(sessionId).catch(() => null);
    try {
      return await AIChatApi.streamTurn(sessionId, data, handlers);
    } catch (error) {
      if (!(error instanceof AIStreamLostError)) {
        throw error;
      }
      // A turn already running at the start is another message's, so its reply isn't this one's
      if (!before || before.session?.turn_in_progress) {
        throw new Error('The connection to the assistant was lost. Try again.');
      }
      handlers.onProgress?.('Still working…');
      return await AIChatApi.waitForReply(sessionId, before.messages.length);
    }
  }

  /** Waits for the running turn to end and returns its reply, the first after seenMessages */
  private static async waitForReply(sessionId: string, seenMessages: number): Promise<AIChatMessageResponse> {
    const deadline = Date.now() + TURN_WAIT_LIMIT_MS;
    while (Date.now() < deadline) {
      // The request may still be on its way to the server, so the first check waits too
      await new Promise((resolve) => setTimeout(resolve, TURN_POLL_INTERVAL_MS));
      const current = await AIChatApi.getSession(sessionId).catch((error: unknown) => {
        if (isSessionGoneError(error)) {
          throw error;
        }
        return null;
      });
      if (current && !current.session?.turn_in_progress) {
        const reply = current.messages.slice(seenMessages).find((message) => message.role === 'assistant');
        if (reply) {
          return { message: reply };
        }
        throw new Error('The assistant could not finish that. Try again.');
      }
    }
    throw new Error('The assistant is taking too long. Try again.');
  }

  /** One turn over SSE. Throws AIStreamLostError if the connection ends before the reply. */
  private static async streamTurn(
    sessionId: string,
    data: SendAIChatMessageRequest,
    handlers: AIChatStreamHandlers
  ): Promise<AIChatMessageResponse> {
    const baseUrl = import.meta.env.VITE_APP_APISERVER || '';
    const cookiePrefix = import.meta.env.VITE_COOKIE_PREFIX;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    const orgId = localStorage.getItem(`${cookiePrefix}_org_id`);
    if (orgId) {
      headers['X-Organization-Id'] = orgId;
    }

    const connection = new AbortController();
    let silence: ReturnType<typeof setTimeout> | undefined;
    const restartSilenceTimer = () => {
      clearTimeout(silence);
      silence = setTimeout(() => connection.abort(), STREAM_SILENCE_LIMIT_MS);
    };
    restartSilenceTimer();

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${BASE_PATH}/sessions/${sessionId}/messages/stream`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(data),
        signal: connection.signal,
      });
    } catch (error) {
      clearTimeout(silence);
      throw new AIStreamLostError(error instanceof Error ? error.message : 'Network error while streaming');
    }

    if (!response.ok || !response.body) {
      clearTimeout(silence);
      throw await refusedStreamError(response);
    }

    let finalResponse: AIChatMessageResponse | null = null;

    const processEvent = (rawEvent: string) => {
      let eventName = '';
      const dataLines: string[] = [];
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice('data:'.length).trimStart());
        }
      }
      if (dataLines.length === 0) return;
      const payload = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;

      switch (eventName) {
        case 'token': {
          const token = (payload as StreamDataEnvelope).data;
          if (typeof token === 'string') {
            handlers.onToken?.(token);
          }
          break;
        }
        case 'status': {
          const status = (payload as StreamDataEnvelope).data;
          if (status === 'validating' || status === 'repairing') {
            handlers.onStatus?.(status);
          } else if (typeof status === 'string') {
            handlers.onProgress?.(status);
          }
          break;
        }
        case 'message': {
          finalResponse = (payload as StreamMessageEnvelope).data ?? null;
          break;
        }
        case 'error':
          throw toAIError(payload);
      }
    };

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read().catch((error: unknown) => {
          throw new AIStreamLostError(error instanceof Error ? error.message : 'Network error while streaming');
        });
        if (done) break;
        restartSilenceTimer();
        buffer += decoder.decode(value, { stream: true });
        let separatorIndex = buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
          const rawEvent = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          processEvent(rawEvent);
          separatorIndex = buffer.indexOf('\n\n');
        }
      }
    } finally {
      clearTimeout(silence);
      reader.cancel().catch(() => undefined);
    }

    if (!finalResponse) {
      throw new AIStreamLostError('The stream ended before the reply');
    }
    return finalResponse;
  }

  /**
   * Attach a file to a Solve session; the server converts it to Markdown. Reading a
   * dense PDF can take a minute or more, so this waits as long as the server does.
   */
  static async addDocument(sessionId: string, file: File): Promise<AIChatDocument> {
    const form = new FormData();
    form.append('file', file);
    return await ApiClient.post<AIChatDocument>(`${BASE_PATH}/sessions/${sessionId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180_000,
    });
  }

  /** A Solve session's documents, oldest first */
  static async listDocuments(sessionId: string): Promise<AIChatDocument[]> {
    return await ApiClient.get<AIChatDocument[]>(`${BASE_PATH}/sessions/${sessionId}/documents`);
  }

  /** Remove a document from a Solve session */
  static async deleteDocument(sessionId: string, documentId: string): Promise<void> {
    await ApiClient.delete(`${BASE_PATH}/sessions/${sessionId}/documents/${documentId}`);
  }

  /**
   * Record thumbs up/down feedback on an assistant message
   */
  static async sendFeedback(sessionId: string, messageId: string, data: SendAIChatFeedbackRequest): Promise<void> {
    await ApiClient.post(`${BASE_PATH}/sessions/${sessionId}/messages/${messageId}/feedback`, data);
  }

  /** A draft session's draft and its latest lint result */
  static async getDraft(sessionId: string): Promise<AIDraftView> {
    return await ApiClient.get<AIDraftView>(`${BASE_PATH}/sessions/${sessionId}/draft`);
  }

  /** Lint the draft again, after the user created what it was blocked on */
  static async recheckDraft(sessionId: string): Promise<AIDraftView> {
    return await ApiClient.post<AIDraftView>(`${BASE_PATH}/sessions/${sessionId}/draft/recheck`);
  }

  /** Confirm, or withdraw, an item lint cannot check (a WhatsApp template, a link) */
  static async confirmDraftItem(sessionId: string, itemId: string, confirmed: boolean): Promise<AIDraftView> {
    return await ApiClient.post<AIDraftView>(`${BASE_PATH}/sessions/${sessionId}/draft/confirm`, {
      item_id: itemId,
      confirmed,
    });
  }

  /**
   * The request that saves a ready draft. The server answers 409 with the draft view
   * while it is not ready, which ApiClient would turn into a bare error, so this reads
   * the response itself.
   */
  static async exportDraft(sessionId: string): Promise<AIDraftExportResult> {
    const response = await axiosInstance.get<{ status: string; data: AIDraftExport | AIDraftView }>(
      `${BASE_PATH}/sessions/${sessionId}/draft/export`,
      { validateStatus: (status) => status === 200 || status === 409 }
    );
    if (response.status === 409) {
      return { ready: false, view: response.data.data as AIDraftView };
    }
    return { ready: true, export: response.data.data as AIDraftExport };
  }

  /** The CSV header a dataset the draft is blocked on needs, as a file */
  static async getDatasetTemplate(sessionId: string, dataset: string): Promise<Blob> {
    return await ApiClient.get<Blob>(
      `${BASE_PATH}/sessions/${sessionId}/draft/dataset-template?dataset=${encodeURIComponent(dataset)}`,
      { responseType: 'blob' }
    );
  }

  /**
   * Get organization AI usage quota status
   */
  static async getQuota(): Promise<AIQuotaStatus> {
    return await ApiClient.get<AIQuotaStatus>(`${BASE_PATH}/quota`);
  }
}
